import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    CheckCircle2,
    Sun,
    Moon,
    ChevronRight,
    GraduationCap
} from 'lucide-react';

export const GroupList = ({ groups, onGroupClick, viewMode = 'grid' }) => {

    return (
        <motion.div
            layout
            className={
                viewMode === 'grid'
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    : "flex flex-col gap-3"
            }
        >
            <AnimatePresence mode='popLayout'>
                {groups.map((group) => (
                    <GroupCard
                        key={group.id}
                        group={group}
                        viewMode={viewMode}
                        onGroupClick={onGroupClick}
                    />
                ))}
            </AnimatePresence>
        </motion.div>
    );
};

const GroupCard = ({ group, viewMode, onGroupClick }) => {
    const confirmedCount = group.students.filter(s => s.confirmed).length;
    const totalCount = group.students.length;
    const progress = Math.round((confirmedCount / totalCount) * 100);
    const isFull = confirmedCount === totalCount;

    // Стили для сетки
    if (viewMode === 'grid') {
        return (
            <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ y: -5, shadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)" }}
                onClick={() => onGroupClick(group)}
                className="group relative cursor-pointer bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-[24px] p-6 hover:bg-slate-800/60 transition-colors"
            >
                <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-indigo-500/10 rounded-2xl text-indigo-400">
                        <GraduationCap size={24} />
                    </div>
                    <StudyTypeBadge type={group.studyType} />
                </div>

                <h3 className="text-xl font-black text-white mb-1 group-hover:text-indigo-400 transition-colors">
                    {group.name}
                </h3>

                <div className="flex items-center gap-2 text-slate-500 text-sm mb-6">
                    <Users size={14} />
                    <span>{totalCount} студентов всего</span>
                </div>

                <div className="space-y-3">
                    <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                        <span className="text-slate-500">Подтверждение</span>
                        <span className={isFull ? "text-emerald-400" : "text-indigo-400"}>
                            {progress}%
                        </span>
                    </div>
                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            className={`h-full rounded-full ${isFull ? 'bg-emerald-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'
                                }`}
                        />
                    </div>
                    <p className="text-[11px] text-slate-500 text-right">
                        {confirmedCount} из {totalCount} чел.
                    </p>
                </div>
            </motion.div>
        );
    }

    // Стили для списка
    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            onClick={() => onGroupClick(group)}
            className="group cursor-pointer bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-2xl p-4 hover:bg-slate-800/60 transition-all flex items-center gap-6"
        >
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform">
                <GraduationCap size={24} />
            </div>

            <div className="flex-grow">
                <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-bold text-white text-lg">{group.name}</h3>
                    <StudyTypeBadge type={group.studyType} isSmall />
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><Users size={14} /> {totalCount}</span>
                    <span className="flex items-center gap-1.5">
                        <CheckCircle2 size={14} className={isFull ? "text-emerald-500" : "text-slate-500"} />
                        {confirmedCount} подтверждено
                    </span>
                </div>
            </div>

            <div className="hidden md:block w-48">
                <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                    <div
                        className={`h-full rounded-full ${isFull ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className="text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all px-2">
                <ChevronRight size={20} />
            </div>
        </motion.div>
    );
};

// Вспомогательный компонент для бейджа обучения
const StudyTypeBadge = ({ type, isSmall = false }) => {
    const isDay = type === 'kunduzgi';
    return (
        <div className={`
            flex items-center gap-1.5 rounded-full font-bold uppercase tracking-tight
            ${isSmall ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-3 py-1'}
            ${isDay
                ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'}
        `}>
            {isDay ? <Sun size={isSmall ? 10 : 12} /> : <Moon size={isSmall ? 10 : 12} />}
            <span>{isDay ? 'Дневное' : 'Вечернее'}</span>
        </div>
    );
};